/**
 * Format currency to IDR format
 * @param {string|number} amount - Amount to format
 * @param {string} currency - Currency code (default: IDR)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'IDR') => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (isNaN(numAmount)) return 'Rp 0';

  if (currency === 'IDR') {
    return `Rp ${numAmount.toLocaleString('id-ID', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })}`;
  }

  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(numAmount);
};

/**
 * Format date to readable format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return '-';

  const date = new Date(dateString);
  return date.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Mask card number
 * @param {string} cardNumber - Card number
 * @returns {string} Masked card number
 */
export const maskCardNumber = (cardNumber) => {
  if (!cardNumber) return '';

  const cleaned = cardNumber.replace(/\s/g, '');
  if (cleaned.length < 4) return cardNumber;

  const last4 = cleaned.slice(-4);
  return `**** **** **** ${last4}`;
};

/**
 * Format card number with spaces
 * @param {string} cardNumber - Card number
 * @returns {string} Formatted card number
 */
export const formatCardNumber = (cardNumber) => {
  const cleaned = cardNumber.replace(/\s/g, '');
  const chunks = cleaned.match(/.{1,4}/g);
  return chunks ? chunks.join(' ') : cleaned;
};

/**
 * Format expiry date MM/YY
 * @param {string} value - Expiry value
 * @returns {string} Formatted expiry
 */
export const formatExpiry = (value) => {
  const cleaned = value.replace(/\D/g, '');

  if (cleaned.length >= 2) {
    return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}`;
  }

  return cleaned;
};

/**
 * Validate card number using Luhn algorithm
 * @param {string} cardNumber - Card number
 * @returns {boolean} Is valid
 */
export const validateCardNumber = (cardNumber) => {
  const cleaned = cardNumber.replace(/\s/g, '');

  if (!/^\d{13,19}$/.test(cleaned)) {
    return false;
  }

  let sum = 0;
  let isEven = false;

  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned[i], 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

/**
 * Get card brand from card number
 * @param {string} cardNumber - Card number
 * @returns {string} Card brand
 */
export const getCardBrand = (cardNumber) => {
  const cleaned = cardNumber.replace(/\s/g, '');

  if (/^4/.test(cleaned)) return 'VISA';
  if (/^5[1-5]/.test(cleaned)) return 'MASTERCARD';
  if (/^3[47]/.test(cleaned)) return 'AMEX';
  if (/^6(?:011|5)/.test(cleaned)) return 'DISCOVER';
  if (/^35/.test(cleaned)) return 'JCB';

  return 'UNKNOWN';
};

/**
 * Mask email
 * @param {string} email - Email address
 * @returns {string} Masked email
 */
export const maskEmail = (email) => {
  if (!email) return '';

  const [username, domain] = email.split('@');
  if (!domain) return email;

  const visibleChars = Math.max(2, Math.floor(username.length * 0.3));
  const masked = username.slice(0, visibleChars) + '*'.repeat(Math.max(0, username.length - visibleChars));

  return `${masked}@${domain}`;
};

/**
 * Mask phone number
 * @param {string} phone - Phone number
 * @returns {string} Masked phone
 */
export const maskPhone = (phone) => {
  if (!phone) return '';

  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length < 4) return phone;

  const start = cleaned.slice(0, 4);
  const end = cleaned.slice(-4);
  const masked = '*'.repeat(Math.max(0, cleaned.length - 8));

  return `${start}${masked}${end}`;
};

/**
 * Generate idempotency key
 * @returns {string} Idempotency key
 */
export const generateIdempotencyKey = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  return `idemp-${timestamp}-${random}`;
};
